import { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable.tsx';
import { LogicServiceLayout, LogicServiceVersion } from '@/services/logicServiceManagementService';
import LogicServiceHistoryTable from './LogicServiceHistoryTable';
import { useSettingsStore } from '@/core/store';

const ROWS_PER_PAGE = 10;

interface LogicServiceMainTableProps {
    layouts: LogicServiceLayout[];
    loading: boolean;
    activeHistoryIds: Set<string>;
    onEdit: (layout: LogicServiceLayout) => void;
    onDownload: (layout: LogicServiceLayout) => void;
    onHistoryToggle: (layoutId: string) => void;
    onHistoryDownload: (layoutName: string, version: LogicServiceVersion) => void;
    onDelete: (layout: LogicServiceLayout) => void;
}

const LogicServiceMainTable = ({
    layouts,
    loading,
    activeHistoryIds,
    onEdit,
    onDownload,
    onHistoryToggle,
    onHistoryDownload,
    onDelete
}: LogicServiceMainTableProps) => {
    const { isMobile } = useSettingsStore();
    const { t } = useTranslation();
    const [page, setPage] = useState(0);

    const columns: DataTableColumn<LogicServiceLayout>[] = useMemo(
        () => [
            {
                text: (
                    <span style={{ fontFamily: 'var(--font-family-medium)', fontWeight: 400, letterSpacing: '1.4px' }}>
                        {t('layout')}
                    </span>
                ),
                value: 'layoutName',
                sortable: true,
                valueGetter: (row) => row.name,
                render: (row) => (
                    <span style={{ fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.name}</span>
                )
            },
            {
                text: (
                    <span style={{ fontFamily: 'var(--font-family-medium)', fontWeight: 400, letterSpacing: '1.4px' }}>
                        {t('version')}
                    </span>
                ),
                value: 'version',
                width: 80,
                sortable: false,
                render: (row) => <span style={{ fontSize: 12 }}>{row.version}</span>
            },
            {
                text: (
                    <span style={{ fontFamily: 'var(--font-family-medium)', fontWeight: 400, letterSpacing: '1.4px' }}>
                        {t('uploadTime')}
                    </span>
                ),
                value: 'uploadTime',
                width: 190,
                sortable: true,
                valueGetter: (row) => row.uploadDateTime,
                render: (row) => {
                    const dt = DateTime.fromISO(row.uploadDateTime).toLocal();
                    return (
                        <span style={{ fontSize: 12 }}>
                            {dt.toFormat('dd.MM.yyyy')}
                            <br />
                            {dt.toFormat('HH:mm')}
                        </span>
                    );
                }
            },
            {
                text: (
                    <span style={{ fontFamily: 'var(--font-family-medium)', fontWeight: 400, letterSpacing: '1.4px' }}>
                        {t('uploadedBy')}
                    </span>
                ),
                value: 'uploadedBy',
                width: 230,
                sortable: true,
                render: (row) => (
                    <span style={{ fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {row.uploadedBy}
                    </span>
                )
            },
            {
                text: (
                    <span style={{ fontFamily: 'var(--font-family-medium)', fontWeight: 400, letterSpacing: '1.4px' }}>
                        {t('changes')}
                    </span>
                ),
                value: 'changes',
                sortable: false,
                render: (row) => (
                    <span style={{ fontSize: 12, whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.changes}</span>
                )
            },
            {
                text: (
                    <span style={{ fontFamily: 'var(--font-family-medium)', fontWeight: 400, letterSpacing: '1.4px' }}>
                        {t('assignedShops')}
                    </span>
                ),
                value: 'assignedShops',
                width: 220,
                sortable: false,
                render: (row) => (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {row.assignedShops.map((shop) => (
                            <Box key={shop.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {shop.configApplied ? (
                                    <i
                                        className="bi bi-check-circle-fill logic-service-shop-applied"
                                        style={{ fontSize: 16, flexShrink: 0 }}
                                    />
                                ) : (
                                    <i
                                        className="bi bi-exclamation-square-fill logic-service-shop-pending"
                                        style={{ fontSize: 16, flexShrink: 0 }}
                                    />
                                )}
                                <span style={{ fontSize: 12 }}>{shop.name}</span>
                            </Box>
                        ))}
                    </Box>
                )
            },
            {
                text: (
                    <span style={{ fontFamily: 'var(--font-family-medium)', fontWeight: 400, letterSpacing: '1.4px' }}>
                        {t('action')}
                    </span>
                ),
                value: 'actions',
                width: 140,
                align: 'left',
                sortable: false,
                isAction: true,
                render: (row) => {
                    const isHistoryActive = activeHistoryIds.has(row.id);
                    const isAssigned = row.assignedShops.length > 0;
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <IconButton size="small" title={t('edit')} onClick={() => onEdit(row)}>
                                <i className="bi bi-pencil" />
                            </IconButton>
                            <IconButton size="small" title={t('download')} onClick={() => onDownload(row)}>
                                <i className="bi bi-download" />
                            </IconButton>
                            {!isMobile && (
                                <IconButton
                                    size="small"
                                    title={t('history')}
                                    disabled={row.history.length === 0}
                                    onClick={() => onHistoryToggle(row.id)}
                                >
                                    <i
                                        className={
                                            isHistoryActive
                                                ? 'bi bi-clipboard-fill logic-service-clipboard--active'
                                                : 'bi bi-clipboard'
                                        }
                                    />
                                </IconButton>
                            )}
                            <Tooltip
                                title={isAssigned ? t('cannotDeleteLayoutAssignedToShops') : ''}
                                placement="top"
                                arrow
                                slotProps={{
                                    tooltip: { className: 'logic-service-action-tooltip' },
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: { offset: [-60, 0] }
                                            }
                                        ]
                                    }
                                }}
                            >
                                <span style={{ marginLeft: '5px' }}>
                                    <IconButton size="small" disabled={isAssigned} onClick={() => onDelete(row)}>
                                        <i className="bi bi-trash3" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                }
            }
        ],
        [t, activeHistoryIds, onEdit, onDownload, onHistoryToggle, onDelete]
    );

    const paginatedLayouts = useMemo(() => {
        const start = page * ROWS_PER_PAGE;
        return layouts.slice(start, start + ROWS_PER_PAGE);
    }, [layouts, page]);

    return (
        // wcn-table--spaced added: gives card-per-row look with gaps between rows (same as MyShops)
        <Box className="logic-service-table-wrapper wcn-table--spaced">
            <NewWcnTable
                columns={columns}
                data={paginatedLayouts}
                loading={loading}
                noDataMessage=""
                size="small"
                sorting={{ defaultSortBy: 'layoutName' }}
                expansion={{
                    getRowKey: (row) => row.id,
                    expandedRows: Array.from(activeHistoryIds),
                    onExpandToggle: (rowKey) => onHistoryToggle(String(rowKey)),
                    showExpandIconCondition: () => false,
                    renderExpandedContent: (row) =>
                        row.history.length > 0 ? (
                            <Box>
                                <LogicServiceHistoryTable layout={row} onHistoryDownload={onHistoryDownload} />
                            </Box>
                        ) : null
                }}
            />

            {layouts.length > ROWS_PER_PAGE && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid var(--sidebar-divider)',
                        pt: 1
                    }}
                >
                    <Box className="wcn-table-pagination">
                        <Box
                            component="nav"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1,
                                fontSize: '13px',
                                color: 'var(--gray-medium)'
                            }}
                        >
                            <span>
                                {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, layouts.length)}{' '}
                                {t('of')} {layouts.length}
                            </span>
                            <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                                <i className="bi bi-chevron-left" style={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={(page + 1) * ROWS_PER_PAGE >= layouts.length}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                <i className="bi bi-chevron-right" style={{ fontSize: 14 }} />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default LogicServiceMainTable;
